/**
 * RevenueCat Service for iOS/Android In-App Purchases
 * 
 * Features:
 * - Native subscription management via StoreKit 2
 * - Remote Paywalls (configured in RevenueCat Dashboard)
 * - Customer Center for subscription management
 * - Entitlement checking for "mind. Pro"
 * 
 * Web users continue using Stripe (see src/services/stripe.js)
 * 
 * @see https://www.revenuecat.com/docs/getting-started/installation/capacitor
 */

import { Capacitor } from '@capacitor/core';

// ============================================================================
// CONFIGURATION
// ============================================================================

// RevenueCat API Keys
// Test key for development - switch to production before App Store release
const REVENUECAT_IOS_API_KEY = 'test_adqrjLFkSwHONrKnHiDYWzEJpQR';
const REVENUECAT_ANDROID_API_KEY = 'YOUR_REVENUECAT_ANDROID_API_KEY'; // Add when ready for Android

// Entitlement identifier - must match what's configured in RevenueCat Dashboard
export const PRO_ENTITLEMENT_ID = 'mind. Pro';

// Product identifiers for manual purchase (if not using Paywalls)
export const PRODUCT_IDS = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly', 
  LIFETIME: 'lifetime',
};

// ============================================================================
// MODULE STATE
// ============================================================================

let Purchases = null;
let RevenueCatUI = null;
let isInitialized = false;
let customerInfoListeners = [];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if running on a native platform (iOS/Android)
 */
export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if RevenueCat is available and should be used
 */
export const isRevenueCatAvailable = () => {
  return isNativePlatform() && isInitialized;
};

/**
 * Get the appropriate API key for the current platform
 */
const getApiKey = () => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    return REVENUECAT_IOS_API_KEY;
  } else if (platform === 'android') {
    return REVENUECAT_ANDROID_API_KEY;
  }
  return null;
};

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize RevenueCat SDK
 * Call this once when the app starts (in native environment only)
 * 
 * @param {string} userId - Firebase user ID to identify the customer
 * @returns {Promise<boolean>} - Whether initialization was successful
 */
export const initializeRevenueCat = async (userId = null) => {
  if (!isNativePlatform()) {
    console.log('[RevenueCat] Not a native platform, skipping initialization');
    return false;
  }

  const apiKey = getApiKey();
  if (!apiKey || apiKey.includes('YOUR_')) {
    console.warn('[RevenueCat] No valid API key for platform:', Capacitor.getPlatform());
    return false;
  }

  try {
    // Dynamic import to avoid bundling issues on web
    const purchasesModule = await import('@revenuecat/purchases-capacitor');
    Purchases = purchasesModule.Purchases;
    const LOG_LEVEL = purchasesModule.LOG_LEVEL;

    // Enable debug logging in development
    if (process.env.NODE_ENV === 'development') {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    }

    // Configure the SDK
    await Purchases.configure({
      apiKey: apiKey,
      appUserID: userId || null, // null = anonymous user
    });

    // Load UI module for Paywalls and Customer Center
    try {
      const uiModule = await import('@revenuecat/purchases-capacitor-ui');
      RevenueCatUI = uiModule.RevenueCatUI;
      console.log('[RevenueCat] UI module loaded for Paywalls & Customer Center');
    } catch (uiError) {
      console.warn('[RevenueCat] UI module not available:', uiError.message);
    }

    // Set up customer info listener for real-time updates
    await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
      console.log('[RevenueCat] Customer info updated:', {
        activeEntitlements: Object.keys(customerInfo.entitlements.active),
      });
      // Notify all listeners
      customerInfoListeners.forEach(listener => listener(customerInfo));
    });

    isInitialized = true;
    console.log('[RevenueCat] ✅ Initialized successfully for platform:', Capacitor.getPlatform());
    
    return true;
  } catch (error) {
    console.error('[RevenueCat] ❌ Initialization error:', error);
    return false;
  }
};

// ============================================================================
// USER MANAGEMENT
// ============================================================================

/**
 * Identify/login a user to RevenueCat
 * Call after Firebase authentication completes
 * 
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Object|null>} - Customer info or null
 */
export const identifyUser = async (userId) => {
  if (!isInitialized || !Purchases) {
    console.warn('[RevenueCat] Cannot identify user - not initialized');
    return null;
  }

  try {
    const { customerInfo } = await Purchases.logIn({ appUserID: userId });
    console.log('[RevenueCat] ✅ User identified:', userId);
    return customerInfo;
  } catch (error) {
    console.error('[RevenueCat] ❌ Error identifying user:', error);
    return null;
  }
};

/**
 * Logout user from RevenueCat
 * Call when Firebase user logs out
 */
export const logoutUser = async () => {
  if (!isInitialized || !Purchases) {
    return;
  }

  try {
    await Purchases.logOut();
    console.log('[RevenueCat] ✅ User logged out');
  } catch (error) {
    console.error('[RevenueCat] Error logging out:', error);
  }
};

// ============================================================================
// ENTITLEMENT & SUBSCRIPTION STATUS
// ============================================================================

/**
 * Get current customer information including subscription status
 * 
 * @returns {Promise<Object|null>} - Customer info object or null
 */
export const getCustomerInfo = async () => {
  if (!isInitialized || !Purchases) {
    return null;
  }

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    console.error('[RevenueCat] Error getting customer info:', error);
    return null;
  }
};

/**
 * Check if user has active "mind. Pro" entitlement
 * 
 * @returns {Promise<boolean>} - Whether user has pro access
 */
export const hasProEntitlement = async () => {
  const customerInfo = await getCustomerInfo();
  if (!customerInfo) return false;
  
  // Check for the pro entitlement
  return customerInfo.entitlements.active[PRO_ENTITLEMENT_ID] !== undefined;
};

/**
 * Get detailed subscription info for pro entitlement
 * 
 * @returns {Promise<Object|null>} - Subscription details or null
 */
export const getProSubscriptionDetails = async () => {
  const customerInfo = await getCustomerInfo();
  if (!customerInfo) return null;

  const proEntitlement = customerInfo.entitlements.active[PRO_ENTITLEMENT_ID];
  if (!proEntitlement) return null;

  return {
    isActive: true,
    productIdentifier: proEntitlement.productIdentifier,
    expirationDate: proEntitlement.expirationDate,
    willRenew: proEntitlement.willRenew,
    periodType: proEntitlement.periodType, // 'normal', 'trial', 'intro'
    isLifetime: proEntitlement.expirationDate === null,
    isSandbox: proEntitlement.isSandbox,
  };
};

/**
 * Add a listener for customer info updates
 * 
 * @param {Function} listener - Callback function that receives customerInfo
 * @returns {Function} - Unsubscribe function
 */
export const addCustomerInfoListener = (listener) => {
  customerInfoListeners.push(listener);
  return () => {
    customerInfoListeners = customerInfoListeners.filter(l => l !== listener);
  };
};

// ============================================================================
// OFFERINGS & PACKAGES
// ============================================================================

/**
 * Get available subscription offerings
 * 
 * @returns {Promise<Object|null>} - Current offering with packages or null
 */
export const getOfferings = async () => {
  if (!isInitialized || !Purchases) {
    console.warn('[RevenueCat] Cannot get offerings - not initialized');
    return null;
  }

  try {
    const { offerings } = await Purchases.getOfferings();
    console.log('[RevenueCat] Offerings fetched:', {
      current: offerings.current?.identifier,
      all: Object.keys(offerings.all || {}),
    });
    return offerings.current;
  } catch (error) {
    console.error('[RevenueCat] Error fetching offerings:', error);
    return null;
  }
};

/**
 * Get all available offerings (for A/B testing, etc.)
 * 
 * @returns {Promise<Object|null>} - All offerings or null
 */
export const getAllOfferings = async () => {
  if (!isInitialized || !Purchases) {
    return null;
  }

  try {
    const { offerings } = await Purchases.getOfferings();
    return offerings.all;
  } catch (error) {
    console.error('[RevenueCat] Error fetching all offerings:', error);
    return null;
  }
};

// ============================================================================
// PURCHASES
// ============================================================================

/**
 * Purchase a subscription package
 * 
 * @param {Object} packageToPurchase - Package object from offerings
 * @returns {Promise<Object>} - Result with success status and customer info
 */
export const purchasePackage = async (packageToPurchase) => {
  if (!isInitialized || !Purchases) {
    throw new Error('RevenueCat not initialized');
  }

  try {
    const { customerInfo } = await Purchases.purchasePackage({
      aPackage: packageToPurchase,
    });

    const isPro = customerInfo.entitlements.active[PRO_ENTITLEMENT_ID] !== undefined;
    
    console.log('[RevenueCat] ✅ Purchase successful:', {
      isPro,
      activeEntitlements: Object.keys(customerInfo.entitlements.active),
    });

    return {
      success: true,
      isPro,
      customerInfo,
    };
  } catch (error) {
    // Handle user cancellation gracefully
    if (error.code === 1 || error.userCancelled) {
      console.log('[RevenueCat] Purchase cancelled by user');
      return { success: false, cancelled: true };
    }
    
    console.error('[RevenueCat] ❌ Purchase error:', error);
    throw error;
  }
};

/**
 * Restore previous purchases
 * Useful after app reinstall or on new device
 * 
 * @returns {Promise<Object>} - Result with success status and whether pro was restored
 */
export const restorePurchases = async () => {
  if (!isInitialized || !Purchases) {
    throw new Error('RevenueCat not initialized');
  }

  try {
    const { customerInfo } = await Purchases.restorePurchases();
    const isPro = customerInfo.entitlements.active[PRO_ENTITLEMENT_ID] !== undefined;
    
    console.log('[RevenueCat] ✅ Purchases restored:', { isPro });
    
    return {
      success: true,
      isPro,
      customerInfo,
    };
  } catch (error) {
    console.error('[RevenueCat] ❌ Restore error:', error);
    throw error;
  }
};

// ============================================================================
// PAYWALLS (Remote UI configured in RevenueCat Dashboard)
// ============================================================================

/**
 * Present the RevenueCat Paywall
 * Uses the paywall configured in RevenueCat Dashboard for current offering
 * 
 * @param {Object} options - Optional configuration
 * @param {Object} options.offering - Specific offering to display (optional)
 * @returns {Promise<Object>} - Result of paywall presentation
 */
export const presentPaywall = async (options = {}) => {
  if (!RevenueCatUI) {
    console.error('[RevenueCat] UI module not available for paywalls');
    return { success: false, error: 'Paywalls not available' };
  }

  try {
    const { result } = await RevenueCatUI.presentPaywall(options);
    
    // Import PAYWALL_RESULT enum
    const { PAYWALL_RESULT } = await import('@revenuecat/purchases-capacitor');
    
    console.log('[RevenueCat] Paywall result:', result);
    
    switch (result) {
      case PAYWALL_RESULT.PURCHASED:
        return { success: true, purchased: true };
      case PAYWALL_RESULT.RESTORED:
        return { success: true, restored: true };
      case PAYWALL_RESULT.CANCELLED:
        return { success: false, cancelled: true };
      case PAYWALL_RESULT.NOT_PRESENTED:
        return { success: false, notPresented: true };
      case PAYWALL_RESULT.ERROR:
        return { success: false, error: true };
      default:
        return { success: false };
    }
  } catch (error) {
    console.error('[RevenueCat] ❌ Paywall error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Present paywall only if user doesn't have pro entitlement
 * Automatically checks entitlement and shows paywall if needed
 * 
 * @returns {Promise<Object>} - Result of paywall presentation
 */
export const presentPaywallIfNeeded = async () => {
  if (!RevenueCatUI) {
    console.error('[RevenueCat] UI module not available for paywalls');
    return { success: false, error: 'Paywalls not available' };
  }

  try {
    const { result } = await RevenueCatUI.presentPaywallIfNeeded({
      requiredEntitlementIdentifier: PRO_ENTITLEMENT_ID,
    });
    
    const { PAYWALL_RESULT } = await import('@revenuecat/purchases-capacitor');
    
    console.log('[RevenueCat] Paywall if needed result:', result);
    
    switch (result) {
      case PAYWALL_RESULT.PURCHASED:
        return { success: true, purchased: true };
      case PAYWALL_RESULT.RESTORED:
        return { success: true, restored: true };
      case PAYWALL_RESULT.NOT_PRESENTED:
        // User already has pro access
        return { success: true, alreadyPro: true };
      case PAYWALL_RESULT.CANCELLED:
        return { success: false, cancelled: true };
      default:
        return { success: false };
    }
  } catch (error) {
    console.error('[RevenueCat] ❌ Paywall if needed error:', error);
    return { success: false, error: error.message };
  }
};

// ============================================================================
// CUSTOMER CENTER (Subscription Management UI)
// ============================================================================

/**
 * Present the Customer Center
 * Allows users to manage their subscription, cancel, restore, etc.
 * 
 * @returns {Promise<void>}
 */
export const presentCustomerCenter = async () => {
  if (!RevenueCatUI) {
    console.error('[RevenueCat] UI module not available for Customer Center');
    throw new Error('Customer Center not available');
  }

  try {
    await RevenueCatUI.presentCustomerCenter();
    console.log('[RevenueCat] Customer Center presented');
  } catch (error) {
    console.error('[RevenueCat] ❌ Customer Center error:', error);
    throw error;
  }
};

// ============================================================================
// SYNC WITH FIRESTORE
// ============================================================================

/**
 * Sync RevenueCat subscription status to Firestore
 * Call this after purchases or when subscription status changes
 * 
 * @param {string} userId - Firebase user ID
 * @param {Object} db - Firestore instance
 * @returns {Promise<boolean>} - Whether sync was successful
 */
export const syncToFirestore = async (userId, db) => {
  if (!userId || !db) {
    console.warn('[RevenueCat] Cannot sync - missing userId or db');
    return false;
  }

  try {
    const { doc, setDoc } = await import('firebase/firestore');
    const customerInfo = await getCustomerInfo();
    
    if (!customerInfo) {
      return false;
    }

    const isPro = customerInfo.entitlements.active[PRO_ENTITLEMENT_ID] !== undefined;
    const proEntitlement = customerInfo.entitlements.active[PRO_ENTITLEMENT_ID];

    await setDoc(doc(db, 'users', userId), {
      tier: isPro ? 'pro' : 'free',
      subscriptionSource: 'revenuecat',
      revenueCatUserId: customerInfo.originalAppUserId,
      subscriptionEnd: proEntitlement?.expirationDate 
        ? new Date(proEntitlement.expirationDate) 
        : null,
      isLifetime: proEntitlement?.expirationDate === null && isPro,
      lastSyncedAt: new Date(),
    }, { merge: true });

    console.log('[RevenueCat] ✅ Synced to Firestore:', { isPro, userId });
    return true;
  } catch (error) {
    console.error('[RevenueCat] ❌ Firestore sync error:', error);
    return false;
  }
};

// ============================================================================
// ATTRIBUTION & ANALYTICS
// ============================================================================

/**
 * Set user attributes for analytics and targeting
 * 
 * @param {Object} attributes - User attributes
 */
export const setUserAttributes = async (attributes) => {
  if (!isInitialized || !Purchases) {
    return;
  }

  try {
    if (attributes.email) {
      await Purchases.setEmail({ email: attributes.email });
    }
    if (attributes.displayName) {
      await Purchases.setDisplayName({ displayName: attributes.displayName });
    }
    console.log('[RevenueCat] User attributes set');
  } catch (error) {
    console.error('[RevenueCat] Error setting user attributes:', error);
  }
};
